import type { Conversation } from '@twilio/conversations';

import { useCallback } from 'react';

import * as Sentry from '@sentry/react-native';

import { getConversationAttributes } from '@south-street-app/utils';
import { useSetPending } from '@utility-nyc/react-query-sdk';

import { ConversationChatView } from '../ChatScreen/ConversationChatView';

const SENTRY_TAG_TYPE = 'screen';
const SENTRY_TAG_CONTEXT = 'SupportConversationContent';

type SupportConversationContentProps = {
  conversation: Conversation | null;
  inlineTitle?: string;
};

const SupportConversationContent = ({
  conversation,
  inlineTitle,
}: SupportConversationContentProps) => {
  const { mutateAsync: setPending } = useSetPending();

  const onSendMessage = useCallback(
    async (message: string) => {
      if (!conversation) {
        return;
      }

      const attributes = getConversationAttributes(conversation);
      const conversationSid = conversation.uniqueName ?? conversation.sid;

      if (
        attributes.status === null ||
        typeof attributes.status === 'undefined' ||
        attributes.status === 'resolved'
      ) {
        try {
          await setPending({ sid: conversationSid });
        } catch (error) {
          Sentry.captureException(error, {
            tags: {
              type: SENTRY_TAG_TYPE,
              context: SENTRY_TAG_CONTEXT,
              method: 'setPending',
            },
            extra: {
              conversationSid,
              status: attributes.status ?? null,
            },
          });

          throw error;
        }

        Object.assign(conversation.attributes as Record<string, unknown>, {
          status: 'pending',
        });
      }

      try {
        await conversation.sendMessage(message);
      } catch (error) {
        Sentry.captureException(error, {
          tags: {
            type: SENTRY_TAG_TYPE,
            context: SENTRY_TAG_CONTEXT,
            method: 'sendMessage',
          },
          extra: {
            conversationSid,
            status: attributes.status ?? null,
          },
        });

        throw error;
      }
    },
    [conversation, setPending],
  );

  return (
    <ConversationChatView
      conversation={conversation}
      emptyMessage={'Trading Desk chat is not available yet.'}
      inlineTitle={inlineTitle}
      onSendMessage={onSendMessage}
      placeholder={'Message'}
    />
  );
};

export { SupportConversationContent };
